import { Component, OnInit, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { Observable, Subject, of, timer } from 'rxjs';
import { UserProfileModel } from 'src/app/auth/models/user-profile.model';
import { filter, takeUntil, flatMap, delay, single } from 'rxjs/operators';
import { Logout, LogoutSuccess, GetUserProfile, SetSelectedUserApp, GetUserApps, SetUserMessageViewed } from 'src/app/auth/state/auth/auth.actions';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material';
import { LegacyMessageService, LegacyEventsEnum } from '../../legacy-message.service';
import { DialogService } from 'src/app/dialogs/dialog.service';
import { UserMessageEnum } from 'src/app/auth/models/user-message-model';
import { AuthService } from 'src/app/auth/auth.service';
import { JoinOrganizationSuccess } from 'src/app/organizations/state/organizations.actions';
import { UserMessageService } from '../../user-message.service';

@Component({
  selector: 'app-user-profile-pin',
  templateUrl: './user-profile-pin.component.html',
  styleUrls: ['./user-profile-pin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfilePinComponent implements OnInit, OnDestroy {
  @Select(AuthState.getLoggedIn)
  loggedIn$: Observable<boolean>;

  @Select(AuthState.getUserProfile)
  userProfile$: Observable<UserProfileModel>;

  initials = '';
  isPremiumAccount = false;
  isGroupAccount = false;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private router: Router,
    private actions: Actions,
    private dialog: MatDialog,
    private legacyService: LegacyMessageService,
    private dialogService: DialogService,
    private userMessageService: UserMessageService
  ) { }

  ngOnInit() {
    // Get user profile
    this.userProfile$
      .pipe(
        filter(profile => profile != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((profile) => {
        this.initials = this.getInitials(profile);
        // user account type
        this.isPremiumAccount = profile.premium_account;
        this.isGroupAccount = profile.organizations && profile.organizations.length > 0;
      });

    // User logs out
    this.actions
      .pipe(
        ofActionDispatched(LogoutSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.router.navigate(['/login']);
      });

    // handle user message queue
    this.initUserMessaging();

    // handle legacy messages
    this.initLegacyRouting();
  }

  initUserMessaging() {
    // First time logging in
    this.getUserMessage(UserMessageEnum.WelcomeTour, 1000, 'v2')
      .pipe(flatMap(() => {
        return this.getUserMessage(UserMessageEnum.DomainAlert, 1000);
      }))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();

    // User joined group
    this.actions
      .pipe(
        ofActionDispatched(JoinOrganizationSuccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.showGroupUpgradeDialog();
      });
  }

  /**
   * Manage legacy user events/messages
   */
  initLegacyRouting() {
    // handle profile updates
    this.legacyService.onEvent(LegacyEventsEnum.ProfileUpdated)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e) => {
        this.store.dispatch(new GetUserProfile());
      });

    // handle account delete
    this.legacyService.onEvent(LegacyEventsEnum.ProfileDeleted)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.handleLogout());

    // handle buy subscription
    this.legacyService.onEvent(LegacyEventsEnum.ShowCheckoutDialog)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.handleBuySubscription());

    // handle upgrade request
    this.legacyService.onEvent(LegacyEventsEnum.ShowUpgradeDialog)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.handleUpgradeSubscription());

    // upgraded: to pro
    this.legacyService.onEvent(LegacyEventsEnum.UserIsActiveProfessional)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e) => {
        this.showPremiumUpgradeDialog();
      });

    // upgraded: to enterprise (group)
    this.legacyService.onEvent(LegacyEventsEnum.UserIsActiveEnterprise)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e) => {
        this.showGroupUpgradeDialog();
      });
      
    // deleted: handle user deleted    
    this.legacyService.onEvent(LegacyEventsEnum.ProfileDeleted)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.handleProfileDeleted());

    // handle close dialog
    this.legacyService.onEvent(LegacyEventsEnum.CloseDialogBoxMessage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.dialog.closeAll());

    // developer app selection
    this.legacyService.onEvent(LegacyEventsEnum.DeveloperAppSelected)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e) => {
        const selectedAppId = e.eventData ? parseInt(e.eventData, 10) : null;
        // sync apps
        this.store.dispatch(new GetUserApps());
        // select app id        
        this.store.dispatch(new SetSelectedUserApp(selectedAppId));
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleLogout() {
    this.store.dispatch(new Logout());
  }

  handleProfile() {
    this.showProfile();
  }

  handleProfileDeleted() {
    this.dialog.closeAll();

    const dialogRef = this.dialogService.openConfirmDialog({
      data: {
        titleText: 'Account Deleted',
        bodyText: 'Your account has been successfully deleted.',
        confirmText: 'Logout',
        confirmColor: 'warn',
        cancelEnabled: false
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        window.location.reload();
      });
  }

  handleGroupAccount() {
    this.showProfile('groups');
  }

  handleDeveloperAccount() {
    this.showProfile('developer');
  }

  handleUpgradeSubscription() {
    this.dialogService.openUpgradeDialog();
  }

  handleBuySubscription() {
    this.dialogService.openBuyDialog();
  }

  handleAbout() {
    this.dialogService.openAboutDialog();
  }

  getInitials(userProfile: UserProfileModel) {
    if (userProfile.first_name) {
      return userProfile.first_name.substr(0, 1);
    }

    if (userProfile.email) {
      return userProfile.email.substr(0, 1);
    }

    return '';
  }

  showProfile(section?: string) {
    this.dialogService.openProfileDialog(null, section);
  }
  
  private showPremiumUpgradeDialog() {
    this.showAccountUpgradeDialog('You have successfuly upgraded to a Premium Account. Reload to reflect changes?');
  }

  private showGroupUpgradeDialog() {
    this.showAccountUpgradeDialog('You have successfuly joined a group. Reload to reflect changes?');
  }

  private showAccountUpgradeDialog(text: string) {
    this.dialog.closeAll();

    const dialogRef = this.dialogService.openConfirmDialog({
      data: {
        titleText: 'Account Upgraded',
        bodyText: text,
        confirmText: 'Reload',
        confirmColor: 'warn',
        cancelEnabled: false
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed) => {
        if (confirmed) {
          window.location.reload();
        }
      });
  }

  /**
   * Manages user messaging queue
   */
  private getUserMessage(message: UserMessageEnum, delayAmount = 0, mode = ''): Observable<boolean> {
    return this.userMessageService.getUserMessage(message, mode)
      .pipe(
        // Allow delay between messages so that we don't spam the user
        delay(delayAmount),

        // Parse message data
        flatMap(({ showUserAlert, messageData, messageSeenId }) => {
          if (showUserAlert && messageData) {
            // Log dialog shown so we don't show repeats
            this.store.dispatch(new SetUserMessageViewed(messageSeenId));

            // Handle which dialog to show
            switch (message) {
              case UserMessageEnum.WelcomeTour:
                return this.dialogService.openWelcomeTourDialog(messageData).afterClosed();
              case UserMessageEnum.DomainAlert:
                return this.dialogService.openJoinDomainDialog(messageData).afterClosed();
            }
          } else {
            return of(false);
          }
        })
      );
  }

}
